import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  FileText,
  Calendar,
  Users,
  Bell,
  MapPin,
  UserCircle,
  Film,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo-white.png";
import FAQSection from "@/components/marketing/FAQSection";
import AppStoreBanner from "@/components/marketing/AppStoreBanner";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { useNativelyEnvironment } from "@/hooks/useNativelyEnvironment";

const Index = () => {
  const { isNativelyApp } = useNativelyEnvironment();

  return (
    <div className="min-h-screen bg-background">
      {/* App Store Banner */}
      <AppStoreBanner />

      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-6xl space-y-12 text-center">
            <div className="space-y-6">
              <h1 className="text-5xl font-bold leading-tight md:text-7xl">
                Plan fast. Shoot smart.
                <br />
                <span className="text-gradient-primary">PROduce more.</span>
              </h1>
              <p className="mx-auto max-w-2xl text-xl text-muted-foreground">
                Festival production management for producers and crew
              </p>
            </div>

            {/* Login Split Section */}
            <div
              className={`grid ${isNativelyApp ? "max-w-md" : "max-w-4xl md:grid-cols-2"} mx-auto gap-6 pt-8`}
            >
              {/* Crew Member Login */}
              <Card className="border-2 bg-card/50 p-8 backdrop-blur-xl transition-all hover:scale-105 hover:border-primary">
                <div className="space-y-6 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary">
                    <UserCircle className="h-10 w-10 text-white" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold">Crew member</h3>
                    <p className="text-muted-foreground">
                      Your schedule and assignments, in the app
                    </p>
                  </div>
                  {/* Not "Crew Login": crew sign in inside the app, and this
                      button leads to the page that hands them the app. */}
                  <Link to="/crew-login" className="block">
                    <Button variant="gradient" size="lg" className="w-full">
                      Get the crew app
                    </Button>
                  </Link>
                </div>
              </Card>

              {/* Producer Login - Hidden in native app */}
              {!isNativelyApp && (
                <Card className="border-2 bg-card/50 p-8 backdrop-blur-xl transition-all hover:scale-105 hover:border-secondary">
                  <div className="space-y-6 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-primary">
                      <Film className="h-10 w-10 text-white" />
                    </div>
                    <div>
                      <h3 className="mb-2 text-2xl font-bold">Producer</h3>
                      <p className="text-muted-foreground">Manage your production and team</p>
                    </div>
                    <Link to="/producer-login" className="block">
                      <Button
                        variant="gradient"
                        size="lg"
                        className="w-full bg-gradient-to-r from-secondary to-primary"
                      >
                        Producer Login
                      </Button>
                    </Link>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24" style={{ backgroundColor: "#050e22" }}>
        <div className="container mx-auto px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">Everything you need</h2>
            <p className="text-lg text-muted-foreground">
              Built for the demands of modern festival production
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={<Sparkles className="h-8 w-8" />}
              title="AI-powered briefings"
              description="Generate comprehensive production briefings with AI assistance in seconds"
              gradient="from-primary to-secondary"
            />
            <FeatureCard
              icon={<FileText className="h-8 w-8" />}
              title="PDF lineup import"
              description="Import artist lineups directly from PDF documents with automatic data extraction"
              gradient="from-secondary to-primary"
            />
            <FeatureCard
              icon={<Calendar className="h-8 w-8" />}
              title="Smart crew scheduling"
              description="Drag-and-drop crew assignment with automatic conflict detection and validation"
              gradient="from-primary to-secondary"
            />
            <FeatureCard
              icon={<Users className="h-8 w-8" />}
              title="Multi-stage management"
              description="Manage multiple stages and artist timetables with real-time crew assignments"
              gradient="from-secondary to-primary"
            />
            <FeatureCard
              icon={<Bell className="h-8 w-8" />}
              title="Mobile crew app"
              description="Crew members receive instant schedule updates and briefings on their mobile devices"
              gradient="from-primary to-secondary"
            />
            <FeatureCard
              icon={<MapPin className="h-8 w-8" />}
              title="GPS crew tracking"
              description="Optional location tracking with interactive festival map visualization for crew coordination"
              gradient="from-secondary to-primary"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24" style={{ backgroundColor: "#050e22" }}>
        <div className="container mx-auto px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">Simple, transparent pricing</h2>
            <p className="mx-auto max-w-2xl text-xl text-muted-foreground">
              Free for crew members. Scale with your productions.
            </p>
          </div>

          <PricingSection />
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Footer */}
      <footer className="border-t border-border py-12" style={{ backgroundColor: "#050e22" }}>
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <img src={logo} alt="PROduce Logo" className="h-8" />
            <div className="flex items-center gap-6">
              <Link
                to="/user-cases"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Use cases
              </Link>
              <Link
                to="/privacy-policy"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/contact"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Contact
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 PROduce. Elevating festival production worldwide.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
}

const FeatureCard = ({ icon, title, description, gradient }: FeatureCardProps) => {
  return (
    <Card className="group border-border bg-card p-6 transition-all hover:scale-105 hover:bg-card/80 hover:shadow-card">
      <div
        className={`h-14 w-14 rounded-xl bg-gradient-to-br ${gradient} mb-4 flex items-center justify-center transition-transform group-hover:scale-110`}
      >
        <div className="text-white">{icon}</div>
      </div>
      <h3 className="mb-2 text-xl font-semibold">{title}</h3>
      <p className="leading-relaxed text-muted-foreground">{description}</p>
    </Card>
  );
};

const PricingSection = () => {
  const { isNativelyApp } = useNativelyEnvironment();
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const prices = {
    lite: { monthly: 29, yearly: 290 },
    pro: { monthly: 99, yearly: 990 },
    studio: { monthly: 199, yearly: 1990 },
  };

  // In native app, show simplified view with only Crew card
  if (isNativelyApp) {
    return (
      <div className="space-y-8">
        <div className="mx-auto max-w-md">
          {/* Crew - Free */}
          <Card className="flex flex-col border-2 border-primary bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-105">
            <div className="mb-6 text-center">
              <Badge variant="secondary" className="mb-4">
                Always free
              </Badge>
              <h3 className="mb-2 text-xl font-bold">Crew</h3>
              <div className="mb-2">
                <span className="text-4xl font-bold">€0</span>
              </div>
              <p className="text-sm text-muted-foreground">Forever</p>
            </div>
            <ul className="mb-6 flex-1 space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>Access all briefings</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>Personal schedule view</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>Real-time notifications</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>Mobile app access</span>
              </li>
            </ul>
            <Link to="/crew-login" className="mt-auto block">
              <Button variant="gradient" className="w-full">
                Get the crew app
              </Button>
            </Link>
          </Card>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          Producer accounts are managed via produce-app.com
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => setBillingInterval("monthly")}
          className={`rounded-full px-6 py-2 font-semibold transition-all ${
            billingInterval === "monthly"
              ? "bg-gradient-to-r from-primary to-secondary text-white"
              : "bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingInterval("yearly")}
          className={`rounded-full px-6 py-2 font-semibold transition-all ${
            billingInterval === "yearly"
              ? "bg-gradient-to-r from-primary to-secondary text-white"
              : "bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          Yearly
          <Badge variant="secondary" className="ml-2">
            Save 2 months
          </Badge>
        </button>
      </div>

      {/* Pricing Cards */}
      <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Crew - Free */}
        <Card className="flex flex-col border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-105">
          <div className="mb-6 text-center">
            <Badge variant="secondary" className="mb-4">
              Always free
            </Badge>
            <h3 className="mb-2 text-xl font-bold">Crew</h3>
            <div className="mb-2">
              <span className="text-4xl font-bold">€0</span>
            </div>
            <p className="text-sm text-muted-foreground">Forever</p>
          </div>
          <ul className="mb-6 min-h-[200px] flex-1 space-y-2">
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Access all briefings</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Personal schedule view</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Real-time notifications</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Mobile app access</span>
            </li>
          </ul>
          <Link to="/crew-login" className="mt-auto block">
            <Button variant="outline" className="w-full">
              Get the crew app
            </Button>
          </Link>
        </Card>

        {/* LITE */}
        <Card className="flex flex-col border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-105">
          <div className="mb-6 text-center">
            <h3 className="mb-2 text-xl font-bold">LITE</h3>
            <div className="mb-2">
              <span className="text-4xl font-bold">€{prices.lite[billingInterval]}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {billingInterval === "monthly" ? "/month" : "/year"}
            </p>
            {billingInterval === "yearly" && (
              <p className="mt-1 text-xs text-muted-foreground">
                €{(prices.lite.yearly / 12).toFixed(2)}/month
              </p>
            )}
          </div>
          <ul className="mb-6 min-h-[200px] flex-1 space-y-2">
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <strong>1 crew member</strong> per production
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Unlimited productions</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>AI-powered scheduling</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Real-time app updates</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Standard support</span>
            </li>
          </ul>
          <Link to="/producer-login" className="mt-auto block">
            <Button variant="outline" className="w-full">
              Start free trial
            </Button>
          </Link>
        </Card>

        {/* PRO */}
        <Card className="relative flex flex-col border-2 border-primary bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-105">
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-secondary">
            Most popular
          </Badge>
          <div className="mb-6 mt-2 text-center">
            <h3 className="mb-2 text-xl font-bold">PRO</h3>
            <div className="mb-2">
              <span className="text-4xl font-bold">€{prices.pro[billingInterval]}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {billingInterval === "monthly" ? "/month" : "/year"}
            </p>
            {billingInterval === "yearly" && (
              <p className="mt-1 text-xs text-muted-foreground">
                €{(prices.pro.yearly / 12).toFixed(2)}/month
              </p>
            )}
          </div>
          <ul className="mb-6 min-h-[200px] flex-1 space-y-2">
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <strong>10 crew members</strong> per production
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <strong>10 productions</strong> per year
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>AI-powered scheduling</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Real-time app updates</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Live crew tracking</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Priority support</span>
            </li>
          </ul>
          <Link to="/producer-login" className="mt-auto block">
            <Button className="mt-auto w-full">Start free trial</Button>
          </Link>
        </Card>

        {/* STUDIO */}
        <Card className="flex flex-col border-2 bg-card/50 p-6 backdrop-blur-xl transition-all hover:scale-105">
          <div className="mb-6 text-center">
            <h3 className="mb-2 text-xl font-bold">STUDIO</h3>
            <div className="mb-2">
              <span className="text-4xl font-bold">€{prices.studio[billingInterval]}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {billingInterval === "monthly" ? "/month" : "/year"}
            </p>
            {billingInterval === "yearly" && (
              <p className="mt-1 text-xs text-muted-foreground">
                €{(prices.studio.yearly / 12).toFixed(2)}/month
              </p>
            )}
          </div>
          <ul className="mb-6 min-h-[200px] flex-1 space-y-2">
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <strong>Unlimited crew members</strong>
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <strong>Unlimited productions</strong>
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Everything in PRO</span>
            </li>
            <li className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Advanced analytics</span>
            </li>
          </ul>
          <Link to="/producer-login" className="mt-auto block">
            <Button variant="outline" className="w-full">
              Start free trial
            </Button>
          </Link>
        </Card>
      </div>

      {/* Enterprise — separate full-width row below the 4-card grid.
          Lives only on the landing page; not in the subscription
          selector inside the producer app. Contact-only, no Stripe
          checkout, no plan_tier in the DB. */}
      <Card
        className="mx-auto mt-6 flex max-w-7xl flex-col gap-6 border-2 bg-card/50 p-8 backdrop-blur-xl transition-all md:flex-row md:items-center md:justify-between"
        data-testid="enterprise-tier-card"
      >
        <div className="flex-1">
          <Badge variant="secondary" className="mb-3">
            Enterprise
          </Badge>
          <h3 className="mb-2 text-2xl font-bold">Custom plan, talk to us</h3>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Everything in the STUDIO tier, plus white-glove support, unlimited live tracking range,
            on-location monitoring support, dedicated database.
          </p>
        </div>
        <a
          href="mailto:sales@produce-app.com?subject=Enterprise%20plan%20enquiry"
          className="md:w-auto"
        >
          <Button size="lg" className="w-full md:w-auto" data-testid="enterprise-tier-contact">
            Contact sales
          </Button>
        </a>
      </Card>
    </div>
  );
};

export default Index;
